import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { onMounted, ref, computed, watch, toRefs } from 'vue';
import { usePreferencesStore } from '@/store/preferences';
import { useSalesStore } from '@/store/sales';
import { useModuleLayoutFetcher } from '@/composables/useModuleLayoutFetcher';
import Table from '@/components/views/TableView.vue';
import MobileCardsView from '@/components/views/MobileCardsView.vue';
import Filter from '@/components/drawer/Filters.vue';
import ViewsContainer from '@/components/views/ViewsContainer.vue';
import { useBottomSheetStore } from '@/store/bottomSheet'; // para confirmar
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useFloatFormStore } from '@/store/floatForm';
import { openFileDialog } from '@/helpers/fileDialog';
import { buildFilePatch } from '@/helpers/buildPatch';
import { buildMergePatch } from '@/utils/deep';
import SubIvaTotalVue from '@/components/reports/cards/SubIvaTotal.vue';
import { useLayoutField } from '@/composables/useLayoutField';

import {
  getPreset,
  getCardPreset,
  UPLOAD_FILE_FIELD
} from '@/components/modules/sales/sales.config';

const moduleName = 'Sales';
const totalsToggleAvailable = true;


export default /*@__PURE__*/_defineComponent({
  __name: 'Sales',
  props: {
    clientId: {},
    client: {},
    view: {}
  },
  setup(__props: any) {

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

// 👉 presets / helpers
const preferencesStore = usePreferencesStore();
const salesStore = useSalesStore();
const bottomSheetStore = useBottomSheetStore();
const floatFormStore = useFloatFormStore();
const layoutFilters = useLayoutField(moduleName, 'filters');
const showTotals = ref(false);
const props = __props;

const { clientId, client, view } = toRefs(props);
const route = useRoute();
// =========================
// Query builder (inyecta defaultQuery del preset + filtro clientId)
// =========================
const buildParams = (params: any) => {
  const preset = getPreset(view.value);
  const base = { ...(preset.defaultQuery || {}), ...(params || {}) };
  if (clientId.value !== undefined && clientId.value !== null) {
    base['filter[client_id]'] = clientId.value;
  }
  if (route.params.id) {
    base['filter[id]'] = route.params.id;
  }
  return base;
};

// =========================
// Fetchers
// =========================
const isCancellations = computed(() => view.value === 'cancellations');

const {
  create,
  update,
  remove,
  exportCsv,
  refresh
} = useModuleLayoutFetcher(
  moduleName,
  (params) => salesStore.fetchSales(buildParams(params)),
  {
    create: isCancellations.value ? undefined : salesStore.createSale,
    update: isCancellations.value ? undefined : ((data) => salesStore.updateSale(data.id, data)),
    remove: isCancellations.value ? undefined : salesStore.deleteSale,
    exportCsv: salesStore.exportCSV
  }
);

const uploadFile = async (item: any, action?: any) => {
  const target = action?.args?.target;            // p.ej. 'additional_data.billing.invoice'
  const label  = action?.args?.label  || 'Archivo';
  const module = action?.args?.module;            // para FileUploader

  // clona fields y cambia módulo en runtime
  const FIELDS = UPLOAD_FILE_FIELD.map(f => (f.value === 'file' ? { ...f, module } : f));

  const extra = await openFileDialog(floatFormStore, { label, fields: FIELDS });
  const url = extra.file;

  // 👇 genera patch por MERGE (no sobreescribe additional_data completo)
  const patch = buildMergePatch(item, target, url);

  // Actualiza
  await salesStore.updateSale(item.id, patch);
  await refresh();
  await fetchSalesTotals();
};

const confirmSale = async (item:any) => {
  bottomSheetStore.openSheet({
    module: 'Sales',
    formTitle: `¿Confirmas que la venta #${item?.id} está lista para continuidad?`,
    props: {
      item: item,
      action: async () => {
        await salesStore.confirmSale(item?.id)
      }
    }
  });
};

const scheduledSale = async (item:any) => {
  console.log(item)
  bottomSheetStore.openSheet({
    module: 'Sales',
    formTitle: `¿Confirmas que la venta #${item?.id} ya esta programada?`,
    props: {
      item: item,
      action: async () => {
        await salesStore.scheduledSale(item?.id, item)
      }
    }
  });
};

const authorizeSale = (item:any) => {
  bottomSheetStore.openSheet({
    formTitle: `Autorizas la venta #${item?.id}?`,
    props: {
      item: item,
      action: async () => {
        await salesStore.authorizeSale({sale_id:item?.id});
      }
    }
  });
};
const authorizeSaleItem = (item:any) => {
  salesStore.authorizeSaleItem(item);
};

const exportInvoiceOrder = (item:any) => {
  salesStore.exportInvoiceOrder(item.id);
};

const resumeSale = async (item: any) => {
  await salesStore.resumeSale(item?.id);
  await refresh();
};

const rowActionHandlers = {
  authorizeSale,
  authorizeSaleItem,
  confirmSale,
  scheduledSale,
  uploadFile,
  exportInvoiceOrder,
  resumeSale
};

const data = computed(() => salesStore.sales);
const salesTotalsData = computed(() => salesStore.sales_totals);

const applyPresetHeaders = () => {
  const preset = getPreset(view.value);
  preferencesStore.setAllModuleHeaders(moduleName, preset.headers);
  preferencesStore.setDefaultModuleHeaders(
    moduleName,
    preset.defaultHeaderOrder.map(v => preset.headers.find((h:any) => h.value === v)).filter(Boolean) as any
  );

  const cardPreset = getCardPreset(view.value);
  preferencesStore.setAllModuleCardFields(moduleName, cardPreset.cardFields);
  const defaultCards = cardPreset.defaultCardOrder
    .map(v => cardPreset.cardFields.find(h => h.value === v))
    .filter(Boolean) as any;
  preferencesStore.setDefaultModuleCardFields(moduleName, defaultCards);
};

onMounted(() => {
  preferencesStore.currentModule = moduleName;
  applyPresetHeaders();
});

watch(view, async (nv) => {
  applyPresetHeaders();
  const preset = getPreset(nv);

  // refresh conserva page/sort actuales; solo sobreescribes lo necesario
  await refresh({
    ...(preset.defaultQuery || {}),
    ...(route.params.id ? { 'filter[id]': route.params.id } : {}),
  });
});

const buildReportParams = () => {
  const preset = getPreset(view.value);
  const params: Record<string, any> = { module_totals: 1, ...(preset.defaultQuery || {}) };

  // Filtros del layout (flatten a filter[key])
  const filters = layoutFilters.value || {};
  Object.entries(filters)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .forEach(([key, value]) => {
      params[`filter[${key}]`] = value;
    });

  if (clientId.value !== undefined && clientId.value !== null) {
    params['filter[client_id]'] = clientId.value;
  }
  if (route.params.id) {
    params['filter[id]'] = route.params.id;
  }
  return params;
};

const fetchSalesTotals = async () => {
  const params = buildReportParams();
  await salesStore.salesTotals(params);
};

watch(
  () => ({
    ...layoutFilters.value,
    view: view.value,
    clientId: clientId.value,
    routeId: route.params.id,
  }),
  fetchSalesTotals,
  { deep: true, immediate: true }
);

const title = computed(() => {
  if (view.value === 'admin') {
    return { singular: 'Facturación', plural: 'Facturación' }; // o “Ventas para facturar”
  }
  if (view.value === 'continuity') {
    return { singular: 'Continuidad', plural: 'Continuidad' };
  }
  if (view.value === 'cancellations') {
    return { singular: 'Cancelación', plural: 'Cancelaciones' };
  }
  return { singular: 'Venta', plural: 'Ventas' };
});




return (_ctx: any,_cache: any) => {
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, null, {
    default: _withCtx(() => [
      (showTotals.value)
        ? (_openBlock(), _createBlock(SubIvaTotalVue, {
            key: 0,
            class: "mb-4",
            cols: 4,
            records: salesTotalsData.value
          }, null, 8, ["records"]))
        : _createCommentVNode("", true),
      (_openBlock(), _createBlock(ViewsContainer, {
        moduleName: moduleName,
        listViewComponent: Table,
        filterComponent: Filter,
        data: data.value,
        config: '@/components/modules/sales/sales.config',
        onCreate: _unref(create),
        onUpdate: _unref(update),
        onDelete: _unref(remove),
        exportCsv: _unref(exportCsv),
        show_toolbar: true,
        show_pagination: true,
        title: title.value,
        rowActionHandlers: rowActionHandlers,
        key: moduleName + '-' + (_unref(view) || 'default'),
        client: _unref(client),
        clientId: _unref(clientId),
        showTotalsToggle: totalsToggleAvailable,
        totalsVisible: showTotals.value,
        onToggleTotals: _cache[0] || (_cache[0] = ($event: any) => (showTotals.value = !showTotals.value))
      }, null, 8, ["data", "onCreate", "onUpdate", "onDelete", "exportCsv", "title", "client", "clientId", "totalsVisible"]))
    ]),
    _: 1
  }))
}
}

})